import {
	Component,
	EventEmitter,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	signal,
	WritableSignal,
} from '@angular/core';

import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';

import { of, Subject } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { AuRightSidebarService } from '@app/core/services/au-right-sidebar.service';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectDetectionTaskResult } from '@app/pages/cmms/components/files/state/files.selectors';
import {
	createDetectionTask,
	retrieveDetectionTask,
} from '@app/pages/cmms/components/files/state/files.action';
import {
	DetectionTaskStatus,
	IDetectionTask,
	IResultData,
} from '@app/pages/cmms/interfaces/incident-detection.interfaces';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';

@Component({
	selector: 'file-detection-button',
	templateUrl: './file-detection-button.component.html',
	styleUrls: ['./file-detection-button.component.scss'],
	imports: [CommonModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule],
})
export class FileDetectionButtonComponent implements OnDestroy, OnInit {
	private readonly sidebarService = inject(AuRightSidebarService);

	@Input() fileEntity: IFileEntity;
	@Input() activeTab: 'view' | 'info';
	@Output() incidentsDetectedAndCreated = new EventEmitter<{
		detected: number;
		created: number;
	}>();
	@Output() getDataExistEmitter = new EventEmitter<boolean>();

	private readonly store$ = inject(Store);
	detectionTask: WritableSignal<boolean> = signal(false);
	noIncidentsDetected: WritableSignal<boolean> = signal(false);
	buttonName: WritableSignal<string> = signal('Detect Incidents');
	getDataExist: WritableSignal<boolean> = signal(false);
	disabledButton: WritableSignal<boolean> = signal(false);
	error: WritableSignal<boolean> = signal(false);
	private destroy$ = new Subject<void>();

	ngOnInit(): void {
		this.store$.dispatch(retrieveDetectionTask({ fileId: String(this.fileEntity.pk) }));

		this.store$
			.select(selectDetectionTaskResult)
			.pipe(
				takeUntil(this.destroy$),
				filter(data => !!data),
				filter(data => this.fileEntity.pk in data),
				switchMap(data => of(data[this.fileEntity.pk]))
			)
			.subscribe(data => {
				if (!data?.results) {
					this.disabledButton.set(true);
					this.buttonName.set('No incidents detected');
					if (data.status === DetectionTaskStatus.RETRIEVED) {
						this.noIncidentsDetected.set(true);
						this.incidentsDetectedAndCreated.emit({
							detected: 0,
							created: 0,
						});
					}
					return;
				}
				if (data.results !== 'error') {
					this.checkResult(data);
				} else if (data.status === DetectionTaskStatus.ERROR) {
					this.error.set(true);
					this.buttonName.set('An Error occurred, please refresh the page');
				}
			});
	}
	detectIncidents() {
		this.disabledButton.set(true);
		this.buttonName.set('Analyzing...');

		this.store$.dispatch(
			createDetectionTask({
				variables: {
					fileId: this.fileEntity.pk,
					locationId: this.fileEntity.locations[0]?.pk,
					performedBy: this.fileEntity['createdBy']['psId'],
					url: this.fileEntity.file.url,
				},
			})
		);
	}

	checkResult(data: IDetectionTask & { status: DetectionTaskStatus }) {
		const isDataRetrieved = data.status === DetectionTaskStatus.RETRIEVED;
		this.getDataExist.set(isDataRetrieved);
		this.getDataExistEmitter.emit(isDataRetrieved);

		const detectionTaskResults = data?.results;
		this.disabledButton.set(!detectionTaskResults);

		if (detectionTaskResults) {
			this.detectionTask.set(true);
			const resultsObject = JSON.parse(detectionTaskResults) as {
				deviation?: IResultData[];
			};
			const resultsCount = resultsObject?.deviation?.length;
			this.buttonName.set(`${resultsCount} incidents detected`);
			const createdElements = resultsObject.deviation.filter(
				item => item.status === 'CREATED'
			);
			this.incidentsDetectedAndCreated.emit({
				detected: resultsCount,
				created: createdElements.length,
			});
		}
	}

	viewSelected() {
		this.sidebarService.viewCurrentEntity(
			ACTIVE_PAGE_CONFIG[CmmsPages.editFile].createComponent,
			CmmsPages.editFile,
			{ data: this.fileEntity, entityType: CmmsPages.fileDetector }
		);
	}

	changeButtonColor() {
		if (this.error()) {
			return 'error-occurred';
		} else if (this.detectionTask()) {
			return 'detected-button-color';
		} else {
			return;
		}
	}
	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
